import { FC, PropsWithChildren, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { type AttachedContentType, type ReduxState } from 'types';
import { ThreadedCommentsByObjectIdProvider } from './ThreadedCommentsByObjectIdContext';

interface Props {
  // Which attached content type these object ids belong to. Its numeric
  // ContentType id is read from Redux, so callers never hardcode it.
  contentTypeKey: AttachedContentType;
  // Object ids to fetch comment threads for. Nullish entries are filtered out,
  // so callers can pass their raw mapped list without pre-cleaning it.
  objectIds: (number | null | undefined)[];
}

/**
 * Reusable wrapper around ThreadedCommentsByObjectIdProvider for any model
 * registered in `attachedContentTypes`. It resolves the content type id from
 * Redux by key and drops nullish object ids, keeping that plumbing out of
 * every page while leaving the underlying provider content-type agnostic.
 *
 * Usage:
 *   <AttachedContentTypeCommentsProvider
 *     contentTypeKey="custom_survey_answer"
 *     objectIds={answerIds}
 *   >
 *     ...
 *   </AttachedContentTypeCommentsProvider>
 */
const AttachedContentTypeCommentsProvider: FC<PropsWithChildren<Props>> = ({
  contentTypeKey,
  objectIds,
  children,
}) => {
  const contentType = useSelector(
    (state: ReduxState) => state.attachedContentTypes[contentTypeKey]
  );

  const resolvedObjectIds = useMemo(
    () => objectIds.filter((objectId): objectId is number => objectId != null),
    [objectIds]
  );

  return (
    <ThreadedCommentsByObjectIdProvider
      contentType={contentType}
      objectIds={resolvedObjectIds}
    >
      {children}
    </ThreadedCommentsByObjectIdProvider>
  );
};

export default AttachedContentTypeCommentsProvider;
